import * as angular from "angular";
import * as _ from "lodash";
import { XeroService } from "../../../services/xero.service";
import { Feature } from "../../../models/feature.model";
import { Limit } from "../../../models/limit.model";
import { Organisation } from "../../../models/organisation.model";
import { OrganisationService } from "../../../services/organisation.service";
import { DialogUtils } from "../../../helper/dialog.helper";
import { PermissionCheck } from "../../../shared/permission";
import { ToastUtils } from "../../../helper/toast.helper";
import {
    languagesConstants,
    LanguageCode,
} from "../../../utils/constants/languages.constants";

let CreateOrganisationComponent = {
    selector: "vcCreateOrganisation",
    templateUrl:
        "/angular/components/admin/dialogs/create-organisation-dialog.component.html",
    bindings: {
        organisation: "=?",
        initialSection: "<?",
    },
    controller: class CreateOrganisationController {
        // * bindings for typescript compiler *
        public organisation?: Organisation;
        public initialSection: string | null = null;
        // ************************************
        /** AV-17974: set once a link/unlink happened, so cancel still updates the row badge. */
        public xeroDirty = false;

        public loading = false;
        public langCodes: LanguageCode[] = languagesConstants.languages;
        public selectedLangCode: LanguageCode | null = null;
        public langCodeSearch: string = "";
        public features = [
            { index: "GEO_CODING", name: "Geocoding", owner: 1 },
            // Off for new organisations; an admin switches it on deliberately.
            { index: "USERLIMIT", name: "User Licenses", owner: 0 },
            { index: "2FA", name: "Two Factor Authentication", owner: 0 },
            { index: "PUBLIC_API", name: "Public API", owner: 0 },
            {
                index: "GO_APP",
                name: "Go App (Whether app selection drop downs display in places)",
                owner: 0,
            },
        ];
        public editableFeatures = [
            "PUBLIC_API",
            "2FA",
            "GO_APP",
            "GEO_CODING",
            "USERLIMIT",
        ];

        public ownerOptions: any[] = [];
        public selectedNewOwner: any = null;
        public ownerSearch: string = "";

        public section = "main";
        /** Built once in $onInit (the shell's binding is an identity watch). */
        public sections: { id: string; label: string; icon: string }[] = [];

        statusPillClass(): string {
            const s = this.organisation && this.organisation.ostatus;
            if (s == "Active") return "ec-pill-success";
            if (s == "Paused") return "ec-pill-error";
            return "ec-pill-neutral";
        }

        public __env: any;
        private dialog: DialogUtils;
        private toast: ToastUtils;
        private modelsUtils: any;
        private service: OrganisationService;
        private permissionProvider: PermissionCheck;

        constructor(
            OrganisationService: OrganisationService,
            DialogUtils: DialogUtils,
            PermissionCheck: PermissionCheck,
            ToastUtils: ToastUtils,
            ModelsUtils: any,
            __app_type: string,
            __env: any
        ) {
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.modelsUtils = ModelsUtils;
            this.service = OrganisationService;
            this.permissionProvider = PermissionCheck;
            this.__env = __env;
        }

        filterLangCodes(query: string): LanguageCode[] {
            const lowerQuery: string = query ? query.toLowerCase() : "";
            return lowerQuery
                ? this.langCodes.filter(
                      (language: LanguageCode): boolean =>
                          language.code.toLowerCase().includes(lowerQuery) ||
                          language.name.toLowerCase().includes(lowerQuery)
                  )
                : this.langCodes;
        }

        $onInit() {
            this.organisation =
                _.cloneDeep(this.organisation) ??
                this.modelsUtils.organisation();
            // Editing gets the section rail; creating scrolls as one page.
            this.sections = this.organisation.oid
                ? [
                      { id: "main", label: "Main details", icon: "badge" },
                      { id: "payment", label: "Payment", icon: "credit_card" },
                      {
                          id: "limits",
                          label: "Limits & features",
                          icon: "tune",
                      },
                  ]
                : [];
            if (
                this.initialSection &&
                this.sections.some((s) => s.id == this.initialSection)
            ) {
                this.section = this.initialSection;
            }

            if (this.organisation.langcode) {
                this.selectedLangCode =
                    this.langCodes.find(
                        (language: LanguageCode): boolean =>
                            language.code === this.organisation.langcode
                    ) ?? null;
            }

            if (this.organisation.oid) {
                this.loadOwnerOptions();
                this.service
                    .limits({
                        id: this.organisation.oid,
                    })
                    .then((response: object) => {
                        const json = response["data"];
                        if (json.data) {
                            this.organisation.limits = json.data;
                        }
                    });

                this.permissionProvider
                    .getAdminFeatures(null, this.organisation.oid)
                    .then((features: Feature[]) => {
                        this.organisation.features = [];
                        angular.forEach(features, (feature: Feature) => {
                            if (
                                this.editableFeatures.includes(
                                    feature.attributes.index
                                )
                            ) {
                                this.organisation.features.push(
                                    feature.attributes
                                );
                            }
                        });
                    });
            } else {
                this.organisation.limits = new Limit({
                    users: 200,
                    superusers: 20,
                    customfields: 10,
                    customfieldscampaign: 10,
                    houseoccupants: 1000000,
                });
                this.organisation.features = _.cloneDeep(this.features);
            }
        }

        /**
         * AV-17961: candidates for an owner handover. Organization Managers
         * (permissionid 9) only - matching the backend guard in
         * OrganisationRepository::changeOwner().
         */
        loadOwnerOptions(): void {
            this.service.adminUsers(this.organisation.oid).then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success && angular.isArray(json.data)) {
                        this.ownerOptions = json.data.filter(
                            (user: any) =>
                                user.permissionid == 9 &&
                                user.uid != this.organisation.oleader
                        );
                    } else {
                        this.toast.error();
                    }
                },
                () => this.toast.error()
            );
        }

        filterOwnerOptions(query: string): any[] {
            const lower = query ? query.toLowerCase() : "";
            return lower
                ? this.ownerOptions.filter(
                      (user: any) =>
                          (user.ufname + " " + user.ulname)
                              .toLowerCase()
                              .includes(lower) ||
                          (user.uemail ?? "").toLowerCase().includes(lower)
                  )
                : this.ownerOptions;
        }

        clearNewOwner(): void {
            this.selectedNewOwner = null;
            this.ownerSearch = "";
        }

        ownerInitials(user: any): string {
            return (
                (user.ufname ? user.ufname[0] : "") +
                (user.ulname ? user.ulname[0] : "")
            ).toUpperCase();
        }

        saveOrganisation(): void {
            if (this.loading == false) {
                this.loading = true;
                this.organisation.langcode = this.selectedLangCode
                    ? this.selectedLangCode.code
                    : null;
                if (this.organisation.oid) {
                    // Send the new owner on the payload only; the row is
                    // updated once the server confirms the handover.
                    const payload = this.selectedNewOwner
                        ? {
                              ...this.organisation,
                              oleader: this.selectedNewOwner.uid,
                          }
                        : this.organisation;
                    this.service
                        .adminUpdate(payload)
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                // Reflect the handover on the list row without
                                // waiting for a reload
                                const owner = this.selectedNewOwner;
                                if (owner) {
                                    this.organisation.oleader = owner.uid;
                                    this.organisation.ufname = owner.ufname;
                                    this.organisation.ulname = owner.ulname;
                                    this.organisation.uemail = owner.uemail;
                                }
                                this.toast.success("Organisation Updated");
                                this.dialog.hide(this.organisation);
                            } else {
                                this.toast.error();
                            }
                        });
                } else {
                    this.service
                        .create(this.organisation)
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                this.organisation = json.data;
                                this.organisation.campaigntimestamp =
                                    json.timestamp;
                                this.toast.success("Organization Created");
                                this.dialog.hide(this.organisation);
                            } else {
                                this.toast.error();
                                this.dialog.hide(json);
                            }
                            this.loading = false;
                        });
                }
            }
        }

        /** AV-17974: keep the list row's badge truthful after a link/unlink. */
        xeroChanged(row: any): void {
            this.xeroDirty = true;
            angular.extend(this.organisation, XeroService.listFields(row));
        }

        cancel(): void {
            if (this.xeroDirty) {
                this.dialog.hide(
                    _.pick(
                        this.organisation,
                        Object.keys(XeroService.listFields(null))
                    )
                );
            } else {
                this.dialog.cancel();
            }
        }
    },
};

angular
    .module("admin")
    .component(
        CreateOrganisationComponent.selector,
        CreateOrganisationComponent
    );
